import { Alert, Button, Card, Form, InputNumber, Select, Space, message } from 'antd';
import { useState } from 'react';
import type { ReactNode } from 'react';

import { ApiError } from '../../api/errors';
import { ProjectHealth, updateProjectProgress } from '../../api/projects';
import type { ProjectDetail, ProjectHealthValue } from '../../api/projects';
import { healthOptions, progressBar } from './projectDisplay';

/** Giá trị của biểu mẫu cập nhật tiến độ. */
interface ProgressFormValues {
  readonly plannedProgressPercent: number;
  readonly actualProgressPercent: number;
  readonly health: ProjectHealthValue;
  readonly healthReason: string | null;
}

/**
 * Thẻ cập nhật tiến độ và tình trạng sức khỏe.
 *
 * <b>Hai con số và một đánh giá đi cùng một lần lưu</b>, vì chúng được điền cùng lúc: người quản lý
 * mở màn hình báo cáo tuần, điền phần trăm rồi đánh giá tình hình ngay đó. Tách làm hai thao tác sẽ
 * tạo ra khoảng thời gian tiến độ đã đổi mà đánh giá thì chưa.
 *
 * <b>Ô lý do hiện ra ngay khi chọn mức khác mức tốt</b>, không đợi tới lúc bấm lưu. Để nó ẩn rồi báo
 * lỗi sau khi lưu là bắt người dùng làm lại một việc mà lẽ ra biểu mẫu đã nói trước.
 */
export function ProjectProgressCard({
  project,
  canEdit,
  onUpdated,
}: {
  readonly project: ProjectDetail;
  readonly canEdit: boolean;
  readonly onUpdated: (updated: ProjectDetail) => void;
}): ReactNode {
  const [form] = Form.useForm<ProgressFormValues>();
  const [isSaving, setIsSaving] = useState(false);
  const [health, setHealth] = useState<ProjectHealthValue>(project.health);

  async function submit(values: ProgressFormValues): Promise<void> {
    setIsSaving(true);

    try {
      const updated = await updateProjectProgress(project.id, {
        plannedProgressPercent: values.plannedProgressPercent,
        actualProgressPercent: values.actualProgressPercent,
        health: values.health,
        healthReason: values.healthReason ?? null,
      });

      onUpdated(updated);
    } catch (error) {
      message.error(error instanceof ApiError ? error.message : 'Không lưu được tiến độ.');
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <Card size="small" title="Tiến độ và tình trạng">
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <div style={{ maxWidth: 420 }}>
          {progressBar(project.plannedProgressPercent, project.actualProgressPercent)}
        </div>

        {canEdit ? (
          <Form<ProgressFormValues>
            form={form}
            layout="inline"
            initialValues={{
              plannedProgressPercent: project.plannedProgressPercent,
              actualProgressPercent: project.actualProgressPercent,
              health: project.health,
              healthReason: project.healthReason,
            }}
            onFinish={(values) => void submit(values)}
          >
            <Form.Item name="plannedProgressPercent" label="Kế hoạch (%)">
              <InputNumber<number> min={0} max={100} style={{ width: 110 }} />
            </Form.Item>
            <Form.Item name="actualProgressPercent" label="Thực tế (%)">
              <InputNumber<number> min={0} max={100} style={{ width: 110 }} />
            </Form.Item>
            <Form.Item name="health" label="Tình trạng">
              <Select<ProjectHealthValue>
                style={{ width: 160 }}
                options={healthOptions}
                onChange={setHealth}
              />
            </Form.Item>
            {health === ProjectHealth.Good ? null : (
              <Form.Item
                name="healthReason"
                label="Lý do"
                rules={[{ required: true, message: 'Chưa ghi lý do.' }]}
                style={{ flex: 1, minWidth: 320 }}
              >
                <Select
                  mode="tags"
                  maxCount={1}
                  placeholder="Camera đặt hàng chậm bốn tuần so với kế hoạch"
                  options={commonHealthReasons}
                />
              </Form.Item>
            )}
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={isSaving}>
                Lưu
              </Button>
            </Form.Item>
          </Form>
        ) : (
          <Alert
            type="info"
            showIcon
            message="Bạn không có quyền cập nhật tiến độ của dự án này."
          />
        )}
      </Space>
    </Card>
  );
}

/**
 * Những lý do hay gặp, để gợi ý chứ không giới hạn.
 *
 * Ô nhập cho phép gõ tự do — đó là lý do dùng chế độ thẻ thay vì ô chọn thuần. Danh sách gợi ý lấy
 * từ những nguyên nhân thật đã gặp ở các dự án tích hợp hệ thống thị giác, và phần lớn nằm ngoài tầm
 * kiểm soát của đội kỹ thuật.
 */
const commonHealthReasons = [
  { value: 'Thiết bị nhập khẩu về chậm so với kế hoạch.' },
  { value: 'Khách chưa dừng được dây chuyền để lắp đặt.' },
  { value: 'Khách đổi yêu cầu sau khi đã chốt phạm vi.' },
  { value: 'Thiếu người: kỹ sư đang bị chia cho dự án khác.' },
  { value: 'Điều kiện ánh sáng tại hiện trường khác với lúc thử nghiệm.' },
  { value: 'Tỷ lệ nhận dạng chưa đạt ngưỡng đã cam kết.' },
];
